/**
 * Staged COGS commits: materials → build → sale (OrdersCogs theoretical ledger).
 */
import { mfgPrimaryBtnClass, mfgSecondaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgDealCogsSnapshotRow, MfgDealFullCogsPreview } from "@/lib/mfgOrdersClient";

const usd = (n: number | null | undefined) =>
  n == null || !Number.isFinite(n)
    ? "—"
    : "$" + n.toLocaleString(undefined, { maximumFractionDigits: 0 });

type Kind = "materials_commit" | "build_commit" | "sale_commit";

const KINDS: Array<{
  kind: Kind;
  label: string;
  desc: string;
  previewValue: (p: MfgDealFullCogsPreview | null) => number | null;
}> = [
  {
    kind: "materials_commit",
    label: "Materials",
    desc: "BOM materials cost locked in",
    previewValue: (p) => p?.materials_cost_usd ?? p?.estimated_materials_cost ?? null,
  },
  {
    kind: "build_commit",
    label: "Build",
    desc: "Labor + overhead locked at build",
    previewValue: (p) => {
      if (!p) return null;
      const labor = p.labor_cost_usd ?? 0;
      const oh = p.applied_overhead_usd ?? 0;
      return labor + oh;
    },
  },
  {
    kind: "sale_commit",
    label: "Sale",
    desc: "Total realized cost & margin at sale",
    previewValue: (p) => p?.full_cost_usd ?? null,
  },
];

export function ManufacturingOrdersCogsCommitLedger({
  fullCogsPreview,
  materialsSnapshot,
  buildSnapshot,
  saleSnapshot,
  commitBusy,
  onCommit,
}: {
  fullCogsPreview: MfgDealFullCogsPreview | null;
  materialsSnapshot: MfgDealCogsSnapshotRow | null;
  buildSnapshot: MfgDealCogsSnapshotRow | null;
  saleSnapshot: MfgDealCogsSnapshotRow | null;
  commitBusy: boolean;
  onCommit: (kind: Kind) => void;
}) {
  const byKind: Record<Kind, MfgDealCogsSnapshotRow | null> = {
    materials_commit: materialsSnapshot,
    build_commit: buildSnapshot,
    sale_commit: saleSnapshot,
  };

  return (
    <div className="space-y-2">
      {KINDS.map((spec, i) => {
        const snap = byKind[spec.kind];
        const prevDone = i === 0 || Boolean(byKind[KINDS[i - 1].kind]);
        const canCommit = !snap && prevDone && Boolean(fullCogsPreview);
        const locked = !snap && !prevDone;
        const preview = spec.previewValue(fullCogsPreview);
        return (
          <div
            key={spec.kind}
            className={`flex flex-wrap items-center gap-3 rounded-xl border px-3 py-2.5 ${
              snap
                ? "border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/20"
                : "border-gray-200 dark:border-zinc-700"
            } ${locked ? "opacity-50" : ""}`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                {spec.label}{" "}
                <span className="font-mono text-[10px] text-gray-400">{spec.kind}</span>
              </div>
              <div className="text-[11px] text-gray-500">{spec.desc}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] text-gray-400">{snap ? "committed" : "preview"}</div>
              <div className="font-mono text-base font-bold text-gray-900 dark:text-zinc-50">
                {usd(snap ? snap.full_cost_usd ?? snap.estimated_materials_cost : preview)}
              </div>
              {snap ? (
                <div className="text-[10px] text-gray-400">
                  {new Date(snap.created_at).toLocaleString()}
                </div>
              ) : null}
            </div>
            {snap ? (
              <span className={`${mfgSecondaryBtnClass} pointer-events-none opacity-80`}>Frozen</span>
            ) : locked ? (
              <span className="text-xs text-gray-400">Locked</span>
            ) : (
              <button
                type="button"
                className={mfgPrimaryBtnClass}
                disabled={commitBusy || !canCommit}
                onClick={() => onCommit(spec.kind)}
              >
                {commitBusy ? "…" : "Commit"}
              </button>
            )}
          </div>
        );
      })}
      <p className="text-[10px] text-gray-500">
        Snapshots are append-only — one per kind per deal. Commit in order: materials → build → sale.
      </p>
    </div>
  );
}
